import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Users, Award, BookOpen, ArrowRight } from 'lucide-react';
import { fadeInUp, staggerContainer } from '@/lib/motion';
import { Button } from '@/components/ui/button';
import { IMAGES } from '@/assets/images';

// Real training session images
const galleryImages = [{
  id: 3,
  title: "Finalna Prezentacja",
  description: "Podsumowanie programu szkoleniowego",
  category: "Prezentacje",
  image: IMAGES.FINAL_PREZENTACJA_01_1_41
}, {
  id: 4,
  title: "Konferencja Branżowa",
  description: "Wystąpienie na konferencji dla specjalistów",
  category: "Konferencje",
  image: IMAGES.NOWA_KONFERENCJA_01_3_42
}, {
  id: 7,
  title: "Warsztat Praktyczny",
  description: "Sesja ćwiczeń praktycznych z uczestnikami",
  category: "Warsztaty",
  image: IMAGES.FINAL_WARSZTAT_01_1_46
}, {
  id: 8,
  title: "Certyfikat Zakończenia",
  description: "Oficjalne potwierdzenie ukończenia szkolenia",
  category: "Certyfikaty",
  image: IMAGES.FINAL_CERTYFIKAT_01_1_47
}, {
  id: 9,
  title: "Szkolenie Biznesowe",
  description: "Sesja szkoleniowa dla kadry zarządzającej",
  category: "Szkolenia",
  image: IMAGES.SZKOLENIE_BIZNESOWE_TRENERKA_OPTIMIZED_1_48
}, {
  id: 10,
  title: "Prezentacja Trenerska",
  description: "Wystąpienie podczas szkolenia dla trainerów",
  category: "Prezentacje",
  image: IMAGES.TRENERKA_PREZENTACJA_COMPRESSED_1_49
}, {
  id: 11,
  title: "Warsztat Interaktywny",
  description: "Sesja z elementami interaktywnymi i ćwiczeniami grupowymi",
  category: "Warsztaty",
  image: IMAGES.WARSZTAT_INTERAKTYWNY_1_50
}, {
  id: 12,
  title: "Warsztat Grupowy",
  description: "Praca w małych grupach nad projektami",
  category: "Warsztaty",
  image: IMAGES.WARSZTAT_01_1_45
}, {
  id: 13,
  image: IMAGES.SZKOLENIE_PREZENTACJA_NOWA_TWARZ_1_53
}, {
  id: 14,
  image: IMAGES.SZKOLENIE_WARSZTAT_NOWA_TWARZ_1_54
}, {
  id: 15,
  image: IMAGES.SZKOLENIE_ONLINE_NOWA_TWARZ_1_51
}, {
  id: 16,
  image: IMAGES.SZKOLENIE_PRAKTYCZNE_NOWA_TWARZ_1_52
}];
export default function Gallery() {
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center max-w-4xl mx-auto">
            <motion.h1 variants={fadeInUp} className="text-5xl lg:text-7xl font-serif text-foreground mb-6">
              Galeria <span className="text-accent">szkoleń</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-muted-foreground leading-relaxed mb-8">
              Zajrzyj za kulisy moich szkoleń i warsztatów. Zobacz, jak wygląda praca 
              z zespołami, liderami i organizacjami w praktyce.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Skontaktuj się <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 lg:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryImages.map((image, index) => <motion.div key={image.id} variants={fadeInUp} custom={index} className="group relative overflow-hidden rounded-2xl bg-background shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={image.image}
                    alt={`Gallery image ${image.id}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 grayscale hover:grayscale-0"
                  />
                </div>

                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center">
                    <Camera className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center max-w-3xl mx-auto mb-16">
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Liczby, które mówią same za siebie
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground">
              Rezultaty mojej pracy ze szkoleniami i rozwojem organizacji.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div variants={fadeInUp} className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl font-bold text-foreground mb-2">5000+</div>
              <div className="text-muted-foreground">Sesji indywidualnych</div>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl font-bold text-foreground mb-2">50+</div>
              <div className="text-muted-foreground">Przeprowadzonych szkoleń</div>
            </motion.div>

            <motion.div variants={fadeInUp} className="text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl font-bold text-foreground mb-2">25+</div>
              <div className="text-muted-foreground">Organizacji współpracujących</div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-primary/5">
        <div className="container mx-auto px-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="text-center max-w-3xl mx-auto">
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-serif text-foreground mb-6">
              Zaplanuj szkolenie dla swojej organizacji
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-muted-foreground leading-relaxed mb-8">
              Skontaktuj się ze mną, aby omówić potrzeby szkoleniowe Twojej organizacji 
              i zaplanować program dostosowany do Waszych celów.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8">
                <a href="https://www.znanylekarz.pl/rachela-nowak/psycholog/kalisz" target="_blank" rel="noopener noreferrer">
                  Omów szczegóły szkolenia <ArrowRight className="ml-2 w-4 h-4" />
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>;
}